import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Card, Badge, Table, Avatar, KPICard } from '@/components/ui';
import { useConstructionDailyReports } from './hooks/useResources';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  submitted: 'bg-amber-100 text-amber-600',
  reviewed: 'bg-blue-100 text-blue-600',
  approved: 'bg-emerald-100 text-emerald-600',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản thảo',
  submitted: 'Đã nộp',
  reviewed: 'Đã soát xét',
  approved: 'Đã phê duyệt',
};

export default function DailyReportListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const {
    reports,
    isLoading,
  } = useConstructionDailyReports(companyId);

  return (
    <PageLayout
      title="Nhật ký Thi công (Daily Reports)"
      description="Theo dõi hoạt động hằng ngày của các tổ đội, sản lượng thi công, tiêu hao vật tư và các sự cố hiện trường."
      breadcrumbs={[{ label: 'Thi công' }, { label: 'Báo cáo ngày' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-file-chart-line">Xuất báo cáo tổng</Button>
          <AuthButton
            module="construction"
            action="create"
            variant="primary"
            icon="ri-add-line"
            onClick={() => navigate('./create')}
          >
            Tạo báo cáo ngày
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map((item) => (
            <Card key={item.id} className="p-0 overflow-hidden hover:shadow-lg transition-all group border-slate-100 flex flex-col h-full" onClick={() => navigate(`./detail/${item.id}`)}>
              <div className="p-5 flex-1">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex flex-col">
                    <span className="text-[10px] text-os-primary font-bold  tracking-widest">{new Date(item.report_date).toLocaleDateString('vi-VN', { weekday: 'long' })}</span>
                    <h3 className="font-bold text-slate-900 mt-1">{new Date(item.report_date).toLocaleDateString('vi-VN')}</h3>
                  </div>
                  <Badge className={`${statusColors[item.status_code]} border-transparent text-[9px] font-bold `}>
                    {statusLabels[item.status_code]}
                  </Badge>
                </div>

                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-os-primary/10 group-hover:text-os-primary transition-all">
                    <i className="ri-group-line text-sm" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] text-slate-400 font-bold ">Tổ đội</span>
                    <span className="text-sm font-bold text-slate-900 underline">{item.team?.name}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold  mb-1">Công việc hoàn thành</p>
                    <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                      {item.completed_work_summary || 'Chưa có thông tin tóm tắt công việc...'}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-sun-cloudy-line text-slate-400" />
                      <span className="text-[10px] font-bold text-slate-500">{item.weather_note || 'Nắng ráo'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-error-warning-line text-red-400" />
                      <span className="text-[10px] font-bold text-slate-500">01 Sự cố</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Avatar name={item.submitter?.full_name} size="xs" />
                  <span className="text-[10px] font-bold text-slate-500  tracking-tighter">{item.submitter?.full_name || '---'}</span>
                </div>
                <Button variant="ghost" size="sm" icon="ri-arrow-right-line" className="text-os-primary text-xs font-bold  tracking-widest p-0">Chi tiết</Button>
              </div>
            </Card>
          ))}

          <button className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-200 rounded-3xl hover:border-os-primary hover:bg-os-primary/5 transition-all group min-h-[300px]">
            <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-os-primary/10 group-hover:text-os-primary transition-all mb-4">
              <i className="ri-add-line text-xl" />
            </div>
            <span className="text-xs font-bold text-slate-400 group-hover:text-os-primary  tracking-widest">Gửi báo cáo mới</span>
          </button>
        </div>
      </div>
    </PageLayout>
  );
}
